import React, { useEffect, useRef } from 'react';
interface RevealProps {
  children: React.ReactNode;
  /** Delay in milliseconds applied once the element enters the viewport. */
  delay?: number;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li' | 'header';
}
const OBSERVER_OPTIONS: IntersectionObserverInit = {
  rootMargin: '0px 0px -8% 0px',
  threshold: 0.05
};
let sharedObserver: IntersectionObserver | null = null;

/**
 * A single observer serves every reveal on the page, and visibility is applied
 * directly to the DOM node so scrolling never triggers React re-renders.
 */
function getSharedObserver(): IntersectionObserver | null {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null;
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('reveal-visible');
        observer.unobserve(entry.target);
      }
    }, OBSERVER_OPTIONS);
  }
  return sharedObserver;
}

/** Fades and lifts content into place the first time it scrolls into view. */
export function Reveal({
  children,
  delay = 0,
  className = '',
  as = 'div'
}: RevealProps) {
  const elementRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;
    const observer = getSharedObserver();
    if (!observer) {
      element.classList.add('reveal-visible');
      return undefined;
    }
    observer.observe(element);
    return () => observer.unobserve(element);
  }, []);
  const Tag = as as React.ElementType;
  return <Tag ref={elementRef} className={`reveal ${className}`} style={delay ? {
    transitionDelay: `${delay}ms`
  } : undefined} data-id="element-22" data-mp="1kb4eqecmwz76">
      {children}
    </Tag>;
}